import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { EventTypeEnum, StageTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { deepEqual } from 'fast-equals';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useParams } from 'react-router-dom';
import { scaleConstants, tournamentEngine } from 'tods-competition-factory';

import { drawGeneratedAtomFamily, eventAtomFamily } from 'atoms/events';
import { tournamentAtom } from 'atoms/tournament';
import SeedingComponent from 'components/panels/eventsSplitDraws/seeding/SeedingComponent';
import { SEEDING } from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/constants';
import { type RatingTypeEnum } from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import RankingAndRatingsSeedItems from 'components/tables/scalings/RankingAndRatingsSeedItems';
import { Feature, featureEnabled } from 'config/clients';
import { getQualifyingDrawsStatus } from 'constants/draws';
import { useAutoSeedDialog } from 'containers/dialogs/AutoSeed';
import useScalings from 'hooks/scalings/useScalings';
import { type FlightProfile, type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { type ScalingType } from 'utils/scalings';

const { RANKING } = scaleConstants;
const { TEAM } = EventTypeEnum;

interface SeedingActions {
  onClearSeeds: (disableToastMessage?: boolean) => Promise<void>;
  onPublishSeeds?: () => Promise<void>;
  onSaveSeeds: () => Promise<void>;
  onUnpublishSeeds?: () => Promise<void>;
}

interface SeedingButtonProps {
  entries: ParticipantInterface[];
  isInEditMode: boolean;
  isSeedingPublished?: boolean;
  loading: boolean;
  seedingActions?: SeedingActions;
  selectedFlight?: FlightProfile;
  onSetIsInEditMode: (isInEditMode: boolean) => void;
}

const SeedingButton: React.FC<SeedingButtonProps> = ({
  entries,
  isInEditMode,
  isSeedingPublished,
  loading,
  seedingActions,
  selectedFlight,
  onSetIsInEditMode,
}) => {
  const { eventId } = useParams<{ eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));
  const drawGenerated = useAtomValue(drawGeneratedAtomFamily(eventId));
  const seedingPopoverAnchorEl = useRef<HTMLButtonElement | null>(null);
  const showAutoSeedDialog = useAutoSeedDialog();

  const drawDefinition = useMemo(() => event?.drawDefinitions?.[0], [event?.drawDefinitions]);
  const drawId = drawDefinition?.drawId || selectedFlight?.drawId;
  const structureId = drawDefinition?.structures?.length && drawDefinition.structures[0].structureId;

  const { seed, availableScalingTypes } = useScalings({ drawId, event });

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { isAddMainDrawToQualifying, isAddQualifyingDrawToMain } = getQualifyingDrawsStatus(
    event?.drawDefinitions?.[0],
  );

  const isTeam = event?.eventType === TEAM;
  const isTableEmpty = entries?.length === 0;
  const isQualifying = entries?.some((entry) =>
    entry?.events?.find((e) => e?.eventId === event?.eventId && e?.entryStage === StageTypeEnum.QUALIFYING),
  );
  const drawStage = isQualifying ? StageTypeEnum.QUALIFYING : StageTypeEnum.MAIN;

  const getPositionAssignments = useCallback(
    () => tournamentEngine.getPositionAssignments({ drawId, structureId })?.positionAssignments,
    [drawId, structureId],
  );
  const positionAssignments = useAtomValue(selectAtom(tournamentAtom, getPositionAssignments, deepEqual));
  const drawSize = positionAssignments?.length;

  const getFlights = useCallback(
    () =>
      tournamentEngine.getFlightProfile({
        eventId: event.eventId,
      })?.flightProfile?.flights,
    [event],
  );

  const flights = useAtomValue(selectAtom(tournamentAtom, getFlights, deepEqual));

  const showSeeding =
    (!drawGenerated && (!flights || flights?.length <= 1)) ||
    selectedFlight ||
    (!isQualifying && isAddMainDrawToQualifying) ||
    (isQualifying && isAddQualifyingDrawToMain);

  const handleChangeIsEdit = useCallback(async () => {
    if (isInEditMode) {
      seedingActions?.onSaveSeeds();
    }
    onSetIsInEditMode(false);
  }, [isInEditMode, onSetIsInEditMode, seedingActions]);

  const handleOpenSeedingPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    seedingPopoverAnchorEl.current = event.currentTarget;
    setIsOpen(true);
  }, []);

  const handleClosePopover = useCallback(() => {
    seedingPopoverAnchorEl.current = null;
    setIsOpen(false);
  }, []);

  const handleAutoSeedingOpen = useCallback(
    (type: string, ratingType?: RatingTypeEnum) => {
      const selectedRatingType = ratingType?.toUpperCase();
      const scalingType = type === RANKING ? 'Ranking' : selectedRatingType;
      const { scaleAttributes, scaleItemsWithParticipantIds } = seed(
        scalingType as ScalingType,
        entries,
        drawSize,
        drawStage,
        selectedFlight && selectedFlight?.drawId,
      );
      const payload = {
        scaleItemsWithParticipantIds,
        context: {
          scaleAttributes: { scaleType: SEEDING },
          scaleBasis: scaleAttributes, // the attributes used to generate seeding
          eventId: event.eventId,
        },
      };
      onSetIsInEditMode(false);
      seedingPopoverAnchorEl.current = null;
      showAutoSeedDialog({ event, ratingType: selectedRatingType, payload, clearSeeds: seedingActions?.onClearSeeds });
      handleClosePopover();
    },
    [
      drawSize,
      drawStage,
      entries,
      event,
      handleClosePopover,
      onSetIsInEditMode,
      seed,
      seedingActions?.onClearSeeds,
      selectedFlight,
      showAutoSeedDialog,
    ],
  );

  const handleManualSeeding = useCallback(() => {
    onSetIsInEditMode(true);
    seedingPopoverAnchorEl.current = null;
    handleClosePopover();
  }, [handleClosePopover, onSetIsInEditMode]);

  const handleRemoveSeeding = useCallback(() => {
    seedingActions?.onClearSeeds();
    seedingPopoverAnchorEl.current = null;
    handleClosePopover();
  }, [handleClosePopover, seedingActions]);

  const handlePublishSeeding = useCallback(() => {
    seedingPopoverAnchorEl.current = null;
    seedingActions?.onPublishSeeds();
    handleClosePopover();
  }, [handleClosePopover, seedingActions]);

  const handleUnpublishSeeding = useCallback(() => {
    seedingPopoverAnchorEl.current = null;
    seedingActions?.onUnpublishSeeds();
    handleClosePopover();
  }, [handleClosePopover, seedingActions]);

  const renderRatingsAndRankingsSeedItems = useCallback(
    () =>
      !isQualifying &&
      featureEnabled(Feature.RANKINGS_AND_RATINGS) && (
        <RankingAndRatingsSeedItems
          handleAutoSeedOpen={handleAutoSeedingOpen}
          isNtrpEnabled={!!availableScalingTypes.find(([sT]) => sT === 'NTRP')}
          isRankingEnabled={!!availableScalingTypes.find(([sT]) => sT === 'Ranking')}
          isWtnEnabled={!!(availableScalingTypes.find(([sT]) => sT === 'WTN') && !isTeam)}
        />
      ),
    [isQualifying, handleAutoSeedingOpen, availableScalingTypes, isTeam],
  );

  return (
    showSeeding && (
      <SeedingComponent
        achorEl={seedingPopoverAnchorEl.current}
        flights={flights}
        isInEditMode={isInEditMode}
        isOpen={isOpen}
        isSeedingPublished={isSeedingPublished}
        isTableEmpty={isTableEmpty}
        loading={loading}
        ratingsAndRankingSeedItems={renderRatingsAndRankingsSeedItems}
        onChangeIsEdit={handleChangeIsEdit}
        onClosePopover={handleClosePopover}
        onManualSeeding={handleManualSeeding}
        onOpenSeedingPopover={handleOpenSeedingPopover}
        onPublishSeeding={handlePublishSeeding}
        onRemoveSeeding={handleRemoveSeeding}
        onUnpublishSeeding={handleUnpublishSeeding}
      />
    )
  );
};

export default SeedingButton;
